import React, { useEffect, useMemo } from "react";
import { BalanceType, ExchangeRateDto, IUserBalanceUpdate, IUserDto } from "interfaces";
import { AppContext, TAppAccessURL } from "./AppContext";
import { useRouter } from "next/router";
import { triggerNotification } from "components/TheComponents/Notification";
import { socket } from "socket";
import {
  EndedRaffleData,
  LeaderboardRaffleStartData,
  Raffle,
  RaffleWithIdData,
} from "types/raffles";
import { LiveStreamData, Stream } from "helpers/types";
import { SocketEvent } from "helpers/constants";
import { useDisclosure } from "@chakra-ui/react";
import useCurrentUser, { useUserClientActions } from "hooks/user/useCurrentUser";
import useClientTrace from "hooks/user/useClientTrace";
import useSocketRevive from "hooks/useSocketRevive";
import { ExceptionHandlingType } from "types/apiServiceTypes";
import apiService from "services/apiService";
import { useShowPageLayoutContext } from "./ShowPageLayoutContext";
import { NotificationData } from "types/notification";
import useFeatureFlags from "hooks/feature-flag/useFeatureFlags";
import { EMPTY_FLAGS } from "constants/commot";
import { ChallengeT } from "types/challenges";
import { useQueryClient } from "@tanstack/react-query";
import { useActiveChallengesCountsKey } from "hooks/challenges/api/useAnyActiveChallenges";

interface IAppProviderProps {
  children?: React.ReactNode;
}

export const AppProvider: React.FC<IAppProviderProps> = ({ children }): JSX.Element => {
  const router = useRouter();
  const { isReviewed } = useShowPageLayoutContext();
  const queryClient = useQueryClient();

  const { logout } = useUserClientActions();
  const { isOpen: isProfileOpen, onOpen: onOpenProfile, onClose: onCloseProfile } = useDisclosure();
  const { isOpen: isLoginOpen, onOpen: onOpenLogin, onClose: onCloseLogin } = useDisclosure();
  const {
    isOpen: isVipTransferOpen,
    onOpen: onOpenVipTransfer,
    onClose: onCloseVipTransfer,
  } = useDisclosure();
  const { data: featureFlags, isFetched: isFeatureFlagsFetched } = useFeatureFlags();

  const [showQrModal, setShowQrModal] = React.useState<boolean>(false);

  useEffect(() => {
    apiService.init(logout);
    return () => {
      socket.off("connect");
      socket.off("disconnect");
    };
  }, [logout]);

  const { data: profile, isFetching: isLoading, refetch } = useCurrentUser();

  useClientTrace(profile?.id);
  useSocketRevive();

  const [userBalance, setUserBalance] = React.useState<
    Pick<IUserDto, "gPointsBalance" | "pointsBalance">
  >({});

  const [newRaffle, setNewRaffle] = React.useState<Raffle | null>(null);
  const [liveStreams, setLiveStreams] = React.useState<Stream[]>([]);
  const [rates, setRates] = React.useState<ExchangeRateDto | null>(null);

  const updateBalance = React.useCallback(async (balance: IUserBalanceUpdate) => {
    if (balance?.balanceType === BalanceType.G_POINT) {
      setUserBalance((oldBalance) => ({
        ...(oldBalance! || {}),
        gPointsBalance: balance.balance,
      }));
    }
    if (balance?.balanceType === BalanceType.POINTS) {
      setUserBalance((oldBalance) => ({
        ...(oldBalance! || {}),
        pointsBalance: balance.balance,
      }));
    }
  }, []);

  useEffect(() => {
    socket.on(SocketEvent.StreamsStatusesCheck, (data) => {
      const onlineStreams = data?.filter((el: Stream) => el.isLive === true);
      setLiveStreams(onlineStreams);
    });

    socket.on(SocketEvent.StreamStatusNotify, (data: NotificationData<LiveStreamData>) => {
      triggerNotification({
        type: "stream-live",
        streamToastProps: data,
      });
      isReviewed.setValue(false);
    });

    socket.on(SocketEvent.NotifyNewCalendarDay, (data: NotificationData<null>) => {
      triggerNotification({
        type: "calendar-day",
        calendarToastProps: data,
      });
      isReviewed.setValue(false);
    });

    socket.on(SocketEvent.NewRaffle, ({ data, notificationData }: NotificationData<Raffle>) => {
      setNewRaffle(data);
      triggerNotification({
        type: "raffle-start",
        toastId: SocketEvent.NewRaffle,
        raffleStartToastProps: {
          data,
          notificationData,
        },
      });
      isReviewed.setValue(false);
    });

    socket.on(
      SocketEvent.ChallengeStart,
      ({ data, notificationData }: NotificationData<ChallengeT>) => {
        triggerNotification({
          type: "challenge-start",
          toastId: SocketEvent.ChallengeStart,
          challengeToastProps: {
            data,
            notificationData,
          },
        });

        queryClient.invalidateQueries({
          queryKey: [useActiveChallengesCountsKey],
        });

        isReviewed.setValue(false);
      }
    );

    socket.on(
      SocketEvent.ChallengeEndSoon,
      ({ data, notificationData }: NotificationData<ChallengeT>) => {
        triggerNotification({
          type: "challenge-end",
          toastId: SocketEvent.ChallengeEndSoon,
          challengeToastProps: {
            data,
            notificationData,
          },
        });
        isReviewed.setValue(false);
      }
    );

    socket.on(SocketEvent.BalanceUpdate, (data) => {
      updateBalance(data);
    });

    socket.on(
      SocketEvent.RaffleRouletteStarts,
      ({ data, notificationData }: NotificationData<RaffleWithIdData>) => {
        triggerNotification({
          toastId: SocketEvent.RaffleRouletteStarts,
          type: "raffle-start",
          raffleStartToastProps: { data, notificationData },
        });
      }
    );

    socket.on(
      SocketEvent.LeaderboardRaffleRouletteStarts,
      ({ data, notificationData }: NotificationData<LeaderboardRaffleStartData>) => {
        triggerNotification({
          toastId: SocketEvent.LeaderboardRaffleRouletteStarts,
          type: "leaderboard-raffle-start",
          leaderboardRaffleToastProps: { data, notificationData },
        });
      }
    );

    socket.on(SocketEvent.NotifyNewNotification, () => {
      isReviewed.setValue(false);
    });

    socket.on(
      SocketEvent.RaffleEndsSoon,
      ({ data, notificationData }: NotificationData<EndedRaffleData>) => {
        triggerNotification({
          type: "raffle-end",
          raffleEndToastProps: { data, notificationData },
        });
      }
    );

    socket.on(
      SocketEvent.ChallengeWin,
      ({ data, notificationData }: NotificationData<ChallengeT>) => {
        triggerNotification({
          type: "challenge-win",
          toastId: SocketEvent.ChallengeWin,
          challengeToastProps: {
            data,
            notificationData,
          },
        });
        isReviewed.setValue(false);
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBalance]);

  const accessFetch = React.useCallback(
    (
      input: string | URL,
      init?: RequestInit,
      exceptionHandlingType: ExceptionHandlingType = ExceptionHandlingType.MANUAL
    ) => apiService.request(input, init, exceptionHandlingType),
    []
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accessURL = React.useCallback(appAccessURL(), []);

  React.useEffect(() => {
    const getStreamsStatuses = async () => {
      const streamsResponse = await accessFetch(
        `/stats/streams-statuses`,
        undefined,
        ExceptionHandlingType.SILENT
      );
      const streamsData = await streamsResponse?.json();
      const onlineStreams = streamsData?.filter((el: Stream) => el.isLive === true);
      setLiveStreams(onlineStreams);
    };

    getStreamsStatuses();
  }, [accessFetch]);

  const onDiscordRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/discord`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onSteamRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/steam`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onKickRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/kick`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onGoogleRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/google`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onTwitchRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/twitch`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onDiscordRefresh = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/discord/refresh`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onTwitchRefresh = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/twitch/refresh`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onKickRefresh = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/connections/kick/refresh`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  // Fetch and subscribe on exchange rate
  const fetchRate = React.useCallback(async () => {
    const url = accessURL(`/balance/rates`) as URL;
    const response = await accessFetch(url.href, undefined, ExceptionHandlingType.SILENT);
    const data = await response?.json();
    setRates(data as ExchangeRateDto);
  }, [accessFetch, accessURL]);

  useEffect(() => {
    fetchRate();
    socket.on(SocketEvent.RatesUpdate, setRates);
  }, [fetchRate]);

  const isFeatureEnabled = React.useCallback(
    (code: string) => featureFlags?.find?.((flag) => flag.code === code)?.enabled ?? false,
    [featureFlags]
  );

  const featureFlagsSafe = featureFlags ?? EMPTY_FLAGS; //featureFlags or the same array to not ruin memoisation
  const isFeatureFlagsReady = featureFlags !== undefined || isFeatureFlagsFetched;

  const mergedProfile = useMemo(
    () => (profile ? { ...profile, ...userBalance } : null),
    [profile, userBalance]
  );

  const contextValue = useMemo(
    () => ({
      apiService,
      accessFetch,
      accessURL,
      showQrModal,
      setShowQrModal,
      profile: mergedProfile,
      featureFlags: featureFlagsSafe,
      isFeatureFlagsReady,
      setRates,
      rates,
      liveStreams,
      newRaffle,
      profileProps: { isProfileOpen, onOpenProfile, onCloseProfile },
      loginModalProps: { isLoginOpen, onOpenLogin, onCloseLogin },
      vipTransferModalProps: { isVipTransferOpen, onOpenVipTransfer, onCloseVipTransfer },
      isFeatureEnabled,
      isProfileLoading: isLoading,
      refetchProfile: refetch,
      setUserBalance,
      onGoogleRedirect,
      onTwitchRedirect,
      onDiscordRedirect,
      onSteamRedirect,
      onKickRedirect,
      onDiscordRefresh,
      onTwitchRefresh,
      onKickRefresh,
    }),
    [
      accessFetch,
      accessURL,
      showQrModal,
      mergedProfile,
      featureFlags,
      isFeatureFlagsReady,
      rates,
      liveStreams,
      newRaffle,
      isProfileOpen,
      onOpenProfile,
      onCloseProfile,
      isLoginOpen,
      onOpenLogin,
      onCloseLogin,
      isVipTransferOpen,
      onOpenVipTransfer,
      onCloseVipTransfer,
      onDiscordRedirect,
      onSteamRedirect,
      onGoogleRedirect,
      isFeatureEnabled,
      onTwitchRedirect,
      onKickRedirect,
      onDiscordRefresh,
      onTwitchRefresh,
      onKickRefresh,
      isLoading,
      refetch,
    ]
  );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

/**
 * Create accessURL binded to domain (from env.NEXT_PUBLIC_APP_BE_ORIGIN)
 * if not one is present in input value
 *
 * @returns TAppAccessURL
 *
 */
function appAccessURL(): TAppAccessURL {
  const urlOrigin: string = process.env.NEXT_PUBLIC_APP_BE_ORIGIN ?? "http://localhost:4000";
  return (input: string, origin: string = urlOrigin): URL => {
    const url = new URL(input, origin);
    return url;
  };
}
